import { format, parseISO, isValid } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import type { TemplateValues, TemplateVariable } from '../types';

/** Doit rester aligné sur `VARIABLE_PATTERN` du service backend. */
const VARIABLE_PATTERN = /\{\{\s*([a-zA-Z]{1,10})\s*:\s*([a-zA-Z0-9_-]{1,40})\s*\}\}/g;

/**
 * Fuseau de référence pour les horaires saisis dans l'administration.
 *
 * L'admin raisonne en heure française (CET l'hiver, CEST l'été) ; c'est la
 * zone, et non un décalage fixe, qui est stockée, pour que le passage à
 * l'heure d'été soit géré tout seul.
 */
export const SOURCE_TIME_ZONE = 'Europe/Paris';

export const EMPTY_TEMPLATE_VALUES: TemplateValues = {
  texts: {},
  debutDate: '',
  debutHeure: '',
  finDate: '',
  finHeure: '',
};

/**
 * Liste les champs déclarés par un contenu, dans leur ordre d'apparition.
 *
 * Les deux langues sont analysées ensemble : un champ appartient au modèle, pas
 * à une traduction. Un motif inconnu n'est pas retenu — il restera affiché tel
 * quel, ce qui rend une faute de frappe visible au lieu de l'escamoter.
 */
export function extractVariables(...contents: string[]): TemplateVariable[] {
  const found = new Map<string, TemplateVariable>();

  for (const content of contents) {
    for (const match of content.matchAll(VARIABLE_PATTERN)) {
      const [, head, tail] = match;

      if (head === 'text') {
        const key = `text:${tail}`;
        if (!found.has(key)) found.set(key, { kind: 'text', name: tail });
        continue;
      }

      if ((head === 'debut' || head === 'fin') && (tail === 'date' || tail === 'heure')) {
        const key = `${head}:${tail}`;
        if (!found.has(key)) found.set(key, { kind: 'period', moment: head, part: tail });
      }
    }
  }

  return [...found.values()];
}

/** Indique si un modèle demande une période (et donc le bloc de saisie dédié). */
export function usesPeriod(variables: TemplateVariable[]): boolean {
  return variables.some((v) => v.kind === 'period');
}

/** Teste la présence d'une partie précise de la période. */
export function hasPeriodPart(
  variables: TemplateVariable[],
  moment: 'debut' | 'fin',
  part: 'date' | 'heure'
): boolean {
  return variables.some((v) => v.kind === 'period' && v.moment === moment && v.part === part);
}

/**
 * Échappe une valeur avant insertion dans le HTML du modèle.
 *
 * Les valeurs sont saisies dans des champs de formulaire, donc du texte brut :
 * sans échappement, un simple « Tarifs < 10 € » casserait le rendu du bandeau.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Formate une date ISO (`YYYY-MM-DD`) dans la langue du contenu.
 *
 * C'est tout l'intérêt d'une date typée : la même valeur produit « 12 octobre
 * 2026 » dans la version française et « October 12, 2026 » dans l'anglaise,
 * sans que l'admin ait à saisir deux fois la date.
 */
export function formatTemplateDate(isoDate: string, language: 'fr' | 'en'): string {
  const parsed = parseISO(isoDate);
  if (!isValid(parsed)) return isoDate;

  return language === 'en'
    ? format(parsed, 'MMMM d, yyyy', { locale: enUS })
    : format(parsed, 'd MMMM yyyy', { locale: fr });
}

/**
 * Normalise la frappe d'un champ d'heure.
 *
 * Renvoie `draft`, ce qu'il faut afficher pendant la saisie, et `value`,
 * l'heure complète et valide à propager — vide tant que les quatre chiffres ne
 * sont pas là. Séparer les deux est ce qui permet de taper « 22h15 » ou
 * « 2215 » sans que le champ ne s'efface à mi-parcours.
 */
export function normalizeHourInput(raw: string): { draft: string; value: string } {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  const draft = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;

  if (draft.length < 5) return { draft, value: '' };

  const [hours, minutes] = draft.split(':').map(Number);
  return { draft, value: hours < 24 && minutes < 60 ? draft : '' };
}

// ============================================
// Horaires : saisis à Paris, lus partout
// ============================================

/** Décalage d'un fuseau, en minutes, à un instant donné (positif à l'est de Greenwich). */
function zoneOffsetMinutes(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  // Certaines implémentations rendent « 24 » pour minuit.
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second')
  );

  return (asUtc - instant.getTime()) / 60_000;
}

/**
 * Convertit une saisie `YYYY-MM-DDTHH:mm` en heure de Paris vers un instant UTC.
 *
 * La date fait partie de la conversion parce qu'elle détermine le décalage : la
 * même heure murale vaut UTC+1 en janvier et UTC+2 en juillet. La double passe
 * rattrape les saisies proches d'un changement d'heure, où le décalage estimé
 * au premier tour n'est pas celui qui s'applique à l'instant final.
 */
export function parisToUtcISO(localDateTime: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(localDateTime);
  if (!match) return null;

  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const wallClock = Date.UTC(year, month - 1, day, hour, minute);

  let utc = wallClock;
  for (let pass = 0; pass < 2; pass++) {
    const offset = zoneOffsetMinutes(new Date(utc), SOURCE_TIME_ZONE);
    utc = wallClock - offset * 60_000;
  }

  const result = new Date(utc);
  return Number.isNaN(result.getTime()) ? null : result.toISOString();
}

/** `HH:mm` tel qu'affiché dans un fuseau donné. */
function clockIn(instant: Date, timeZone: string | undefined, locale: string): string {
  return instant.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/** Jour civil (`YYYY-MM-DD`) observé dans un fuseau donné. */
function dayIn(instant: Date, timeZone?: string): string {
  return instant.toLocaleDateString('en-CA', timeZone ? { timeZone } : undefined);
}

/**
 * Rend un instant à l'heure du lecteur.
 *
 * La date n'est ajoutée que si le fuseau du lecteur le fait basculer un autre
 * jour que Paris : « 22:00 » à Paris devient « 05:00 le 13 octobre » à Tokyo, et
 * afficher la seule heure induirait en erreur sur le jour concerné.
 */
export function formatLocalTime(instant: Date, language: 'fr' | 'en'): string {
  const locale = language === 'en' ? 'en-US' : 'fr-FR';
  const time = clockIn(instant, undefined, locale);

  if (dayIn(instant) === dayIn(instant, SOURCE_TIME_ZONE)) {
    return time;
  }

  // Mois en toutes lettres : l'abréviation française (« 13 oct. ») ajoute un
  // point qui en colle un second quand la variable termine la phrase.
  const day = instant.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  return language === 'en' ? `${time} on ${day}` : `${time} le ${day}`;
}

/**
 * Remplace les horaires publiés par leur équivalent dans le fuseau du lecteur.
 *
 * La conversion a lieu ici, à l'affichage, et non à la publication : le serveur
 * ne connaît pas le fuseau de chaque lecteur. Le contenu publié porte donc
 * l'instant UTC dans `datetime`, et le texte de la balise — l'heure de Paris —
 * sert de repli si cette fonction ne s'exécute pas.
 */
export function localizeTimes(html: string, language: 'fr' | 'en'): string {
  if (!html.includes('data-ann-time') || typeof DOMParser === 'undefined') {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('time[data-ann-time]').forEach((el) => {
    const instant = new Date(el.getAttribute('datetime') || '');
    if (Number.isNaN(instant.getTime())) return;

    const parisClock = clockIn(instant, SOURCE_TIME_ZONE, 'fr-FR');
    el.textContent = formatLocalTime(instant, language);
    el.setAttribute(
      'title',
      language === 'en' ? `${parisClock} Paris time` : `${parisClock} heure de Paris`
    );
  });

  return doc.body.innerHTML;
}

/** Balise `<time>` portant l'instant UTC, avec l'heure de Paris comme repli. */
function timeMarkup(day: string, hour: string): string | null {
  const iso = parisToUtcISO(`${day}T${hour}`);
  if (!iso) return null;

  const parisClock = clockIn(new Date(iso), SOURCE_TIME_ZONE, 'fr-FR');
  return `<time datetime="${iso}" data-ann-time="">${escapeHtml(parisClock)}</time>`;
}

/**
 * Remplace les motifs d'un contenu par les valeurs saisies.
 *
 * Un champ laissé vide garde son motif : le contenu produit montre alors
 * clairement ce qui reste à remplir, plutôt que d'ouvrir un trou silencieux
 * dans la phrase. Une date de fin vide retombe sur celle du début — c'est le
 * cas courant d'un événement qui commence et finit le même jour.
 */
export function applyVariables(
  content: string,
  values: TemplateValues,
  language: 'fr' | 'en'
): string {
  const finDate = values.finDate || values.debutDate;

  return content.replace(VARIABLE_PATTERN, (match, head: string, tail: string) => {
    if (head === 'text') {
      const value = values.texts[tail];
      return value ? escapeHtml(value) : match;
    }

    if (head === 'debut' || head === 'fin') {
      const day = head === 'debut' ? values.debutDate : finDate;
      if (!day) return match;

      if (tail === 'date') {
        return escapeHtml(formatTemplateDate(day, language));
      }

      if (tail === 'heure') {
        const hour = head === 'debut' ? values.debutHeure : values.finHeure;
        if (!hour) return match;
        return timeMarkup(day, hour) ?? match;
      }
    }

    return match;
  });
}
